/**
 * Public Magic QR onboarding — customer scans QR from a shopkeeper's counter
 * and submits their name/mobile/DOB. Data auto-saves to shopkeeper's CRM.
 * No login required.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Store, CheckCircle2, ShieldCheck, Star } from 'lucide-react';

// Tier styling helpers — 3 colors: emerald (verified) / blue (trusted) / amber gold (elite)
const TIER_STYLES = {
  elite:     { chip: 'bg-amber-500/15 border-amber-400/50 text-amber-200',   ring: 'ring-amber-400/50',   icon: 'text-amber-300',   star: 'text-amber-300 fill-amber-300' },
  trusted:   { chip: 'bg-blue-500/15 border-blue-400/50 text-blue-200',       ring: 'ring-blue-400/50',    icon: 'text-blue-300',    star: 'text-blue-300 fill-blue-300' },
  verified:  { chip: 'bg-emerald-500/15 border-emerald-400/50 text-emerald-200', ring: 'ring-emerald-400/50', icon: 'text-emerald-300', star: 'text-emerald-300 fill-emerald-300' },
  unverified:{ chip: 'bg-slate-500/15 border-slate-400/30 text-slate-300',    ring: 'ring-slate-400/30',   icon: 'text-slate-400',   star: 'text-slate-500' },
};

const TrustStars = ({ score, className = '' }) => {
  const full = Math.floor(score);
  const half = score - full >= 0.5;
  return (
    <span className={`inline-flex items-center gap-0.5 ${className}`} aria-label={`Trust score ${score}/5`}>
      {[0,1,2,3,4].map((i) => (
        <Star key={i} className={`w-3 h-3 ${i < full ? 'fill-current' : i === full && half ? 'fill-current opacity-60' : 'opacity-30'}`} />
      ))}
    </span>
  );
};
import api from '../../utils/api';

export default function VyaparMagicQR() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ name: '', mobile: '', whatsapp: '', dob: '' });
  const [submitted, setSubmitted] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/vyapar/magic-qr/public/${token}`)
      .then((r) => setInfo(r.data))
      .catch(() => setInfo({ error: true }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) return toast.error('Naam aur mobile zaroori hai');
    setBusy(true);
    try {
      const r = await api.post(`/vyapar/magic-qr/public/${token}/submit`, form);
      setSubmitted(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Kuch galat hua'); }
    finally { setBusy(false); }
  };

  if (!info) return <div className="min-h-screen flex items-center justify-center text-white">Loading…</div>;
  if (info.error) return <div className="min-h-screen flex items-center justify-center text-white">Invalid QR code</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-fuchsia-950 to-slate-950 text-white flex items-center justify-center p-4" data-testid="magic-qr-public">
      <div className="w-full max-w-md rounded-2xl border border-fuchsia-400/30 bg-slate-900/80 p-6">
        <div className="text-center mb-4">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-pink-500 items-center justify-center mb-2">
            <Store className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-xl font-extrabold flex items-center justify-center gap-1.5">
            {info.business_name}
            {info.verified && (
              <span title={`${info.tier_label} · ${info.trust_score}/5`} data-testid="verified-badge" className={`inline-flex items-center gap-0.5 ${(TIER_STYLES[info.tier] || TIER_STYLES.verified).icon}`}>
                <ShieldCheck className="w-5 h-5" />
              </span>
            )}
          </h2>
          {info.verified ? (
            <div className={`inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full border font-bold text-[11px] ${(TIER_STYLES[info.tier] || TIER_STYLES.verified).chip}`} data-testid="verified-badge-pill">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>{info.tier_label} by Anant Network</span>
              <span className="opacity-70">·</span>
              <TrustStars score={info.trust_score || 0} className={(TIER_STYLES[info.tier] || TIER_STYLES.verified).star} />
              <span className="ml-1 font-mono">{(info.trust_score || 0).toFixed(1)}</span>
            </div>
          ) : null}
          <p className="text-slate-400 text-sm mt-2">Namaste! Kripya apni details bharein 🙏</p>
        </div>
        {submitted ? (
          <div className="text-center py-6" data-testid="magic-qr-success">
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-3" />
            <div className="text-lg font-bold">{submitted.welcome_message}</div>
            {submitted.already_exists && <div className="text-xs text-slate-400 mt-2">Aap already registered hain.</div>}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <input required placeholder="Aapka naam *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="mqr-name" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
            <input required type="tel" placeholder="Mobile number *" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="mqr-mobile" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
            <input type="tel" placeholder="WhatsApp (agar alag hai)" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
            <input type="date" placeholder="Janmdin" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
            <button type="submit" disabled={busy} data-testid="mqr-submit" className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-bold text-sm disabled:opacity-60">{busy ? 'Bhej rahe hain…' : 'Submit karein'}</button>
          </form>
        )}
        <div className="text-center text-[10px] text-slate-500 mt-4">Powered by Anant Smart Vyapar System</div>
      </div>
    </div>
  );
}
